import React, { useCallback, useEffect, useState } from "react";
import { FilePlus2, FileText, RefreshCw, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { useAuth } from "../Auth";

const GuttenbergProjectPanel = ({ projectId, project }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUseGuttenberg = Boolean(user?.can_use_guttenberg);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      setReports(await api.listProjects(projectId));
      setStatus("");
    } catch (error) {
      setStatus(error.message || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setLoading(true);
    setStatus("Création du rapport et snapshot Deepflow...");
    try {
      const report = await api.createFromDeepflow(projectId, {
        title: `Rapport - ${project?.project_number || project?.name || `Projet ${projectId}`}`,
        client: project?.client_name || "",
        author: "Innowide",
        report_type: "rapport technique",
      });
      navigate(`/tools/guttenberg/reports/${report.id}`);
    } catch (error) {
      setStatus(error.message || "Création impossible");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (report) => {
    if (!report?.id || deletingReportId) return;
    const confirmed = window.confirm(`Supprimer définitivement le rapport "${report.title}" ?`);
    if (!confirmed) return;
    setDeletingReportId(report.id);
    setStatus("Suppression du rapport...");
    try {
      await api.deleteProject(report.id);
      setReports((current) => current.filter((item) => item.id !== report.id));
      setStatus("Rapport supprimé.");
    } catch (error) {
      setStatus(error.message || "Suppression impossible");
    } finally {
      setDeletingReportId("");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Outils</h2>
          <p className="text-sm text-slate-500">Lancez Guttenberg avec les données Deepflow du projet.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm hover:border-slate-300"
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
          {canUseGuttenberg && <button
            type="button"
            onClick={create}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:border-blue-300 hover:bg-white"
            disabled={loading}
          >
            <FilePlus2 className="h-4 w-4" />
            Nouveau rapport Guttenberg
          </button>}
        </div>
      </div>

      {status && <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">{status}</div>}
      {!canUseGuttenberg && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Accès en lecture seule aux rapports Guttenberg.
        </div>
      )}

      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {reports.map((report) => (
          <article
            key={report.id}
            className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 transition hover:border-blue-200 hover:bg-white"
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(`/tools/guttenberg/reports/${report.id}`)}
                className="min-w-0 flex-1 text-left"
                disabled={deletingReportId === report.id}
              >
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{report.title}</div>
                    <div className="mt-1 text-xs text-slate-500">{report.source_files?.length || 0} source(s)</div>
                  </div>
                </div>
              </button>
              {canUseGuttenberg && <button
                type="button"
                onClick={() => deleteReport(report)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={Boolean(deletingReportId)}
                title="Supprimer le rapport"
                aria-label={`Supprimer ${report.title}`}
              >
                <Trash2 className={`h-4 w-4 ${deletingReportId === report.id ? "animate-pulse" : ""}`} />
              </button>}
            </div>
          </article>
        ))}
        {!loading && reports.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-500">
            Aucun rapport Guttenberg lié à ce projet.
          </div>
        )}
      </div>
    </div>
  );
};

export default GuttenbergProjectPanel;
